import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import Input from "../../forms/Input";
import PasswordInput from "../../forms/PasswordInput";
import ChangePasswordForm from "./ChangePasswordForm";
import Checkbox from "../../forms/Checkbox";
import Button from "../../buttons/Button";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import withRouter from "../../utils/withRouter";
import withStorage from "../../utils/withStorage";
import withRequests from "../../utils/withRequests";
import LoadingPanel from "../../LoadingPanel";
import CoraError from "../../utils/coraError";
import { getClientVersion, afterLoginHandle, getURLParamsByNames } from "../../utils/helper";
import "./FullscreenLogin.scss";
import { useDidMount } from "../../utils/hooks";

const USER_KEY = 'User';
const USERNAME_KEY = "username";
/**
 * Login component
 * @module
 * @param {object} [user] - User
 * @param {bool} isLoading - Is loading?
 * @param {bool} [rememberDisabled] - Remember disabled
 * @param {func} signIn - Sign in function
 * @param {object} [afterLoginlocation] - After login location
 * @param {string} [userNameLabel] - User name label, "Prihlasovacie meno" if not provided
 * @param {string} [passwordLabel] - Password label, "Heslo" if not provided
 * @param {string} [logoUrl] - Logo url
 * @param {string} [backgroundUrl] - Background image url
 * @param {string} [appTitle] - Application title
 * @param {string} [appFullName] - Application full name
 * @param {bool} [showForgotPassword] - Show button for forgot Password
 * @param {func} [showMsg] - Function to show message to user
 */
const Login = (props) => {
  const username = props.rememberDisabled
    ? ""
    : localStorage.getItem(USERNAME_KEY);

  const { guid, code, provider, expired, rt } = getURLParamsByNames([
    "guid",
    "code",
    "provider",
    "expired",
    "rt"
  ]);

  const [state, setState] = useState({
    isLoading: false,
    username: username || "",
    password: null,
    matchPassword: "",
    remember: username !== null,
    guid,
    code,
    provider,
    changePassword: false,
    changePasswordText: null,
    expired,
    isOpenAlert: false,
    rt
  });

  const navigate = useNavigate();

  const reset = (username) => {
    setState({
      isLoading: false,
      username: username || "",
      password: "",
      matchPassword: "",
      remember: false,
      guid: null,
      code: null,
      provider: null,
      changePassword: false,
      changePasswordPattern: null,
      changePasswordText: null,
      expired: null,
      isOpenAlert: false,
      rt: null
    })
  }

  useDidMount(async () => {
    const { guid, code, provider, rt } = state;
    const { verifyGUID, oAuthLogin } = props;

    if (code && provider) {
      await oAuthLogin(props, code, provider);

      if (props.user !== null) return;
      navigate("/");
    }
    else if (guid) {
      await verifyGUID(guid);
      navigate("/");
    }
    else if (code) {
      getChangePasswordText();
      setState(prevState => ({ ...prevState, changePassword: true }));
    }
    else if (rt) {
      await redirect(rt);
      navigate("/");
    }
  })

  const redirect = async (token) => {
    await props.requests.signInRedirect(token);
    const user = sessionStorage.getItem(USER_KEY);
    if (user) window.location.reload();
  }

  const getChangePasswordText = async () => {
    setState(prevState => ({ ...prevState, isLoading: true }))
    try {
      const pattern = await props.requests.getPattern(state.code)
      setState(prevState => ({ ...prevState, changePasswordText: pattern?.PatternText, changePasswordPattern: pattern?.Pattern }))
    }
    catch (error) {
      if (error instanceof CoraError && error.code === "401.000") {
        props.showMsg("Odkazu na zmenu hesla vypršala platnosť.", "error")
        navigate("/")
      }
      else {
        props.showMsg("Neočakávaná chyba.", "error")
      }
    }
    setState(prevState => ({ ...prevState, isLoading: false }))
  }

  const handleChange = (name, value) => {
    setState(prevState => ({ ...prevState, [name]: value }));
  }


  const handleCloseDialog = () => {
    setState(prevState => ({ ...prevState, isOpenAlert: false }))
  }

  const handleCreateCode = async () => {
    setState(prevState => ({ ...prevState, isLoading: true }))
    const username = state.username.trim();
    try {
      await props.requests.createCode(username);
      setState(prevState => ({ ...prevState, isOpenAlert: true }))
    }
    catch (error) {
      if (error instanceof CoraError) {
        props.showMsg(error.message, "error")
      }
      else {
        props.showMsg("Neočakávaná chyba.", "error")
      }
    }
    setState(prevState => ({ ...prevState, isLoading: false }))
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const username = state.username.trim();
    const password = state.password;

    await props.signIn(username, password);
  };

  useEffect(() => {
    if (props.user !== null) {
      afterLoginHandle(props, state.remember ? state.username : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user])


  const renderLogin = () => {
    const { children } = props;
    if (state.changePassword) {
      return (
        <ChangePasswordForm
          pattern={state.changePasswordPattern}
          patternText={state.changePasswordText}
          children={props.children}
          isLoading={props.isLoading}
          showMsg={props.showMsg}
          code={state.code}
          reset={reset}
        />
      )
    }
    else if (state.rt) {
      return (
        <form className="login-form">
          <div className="login-title">Prihlásenie</div>
          <div className="login-field">
            <span>Preberá sa prihlásenie z ekosystému CORA</span>
          </div>
        </form>
      );
    }
    else {
      return (
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-title">Prihlásenie</div>
          <div className="login-label">{props.userNameLabel ?? "Prihlasovacie meno"}</div>
          <div className="login-field">
            <Input
              name="username"
              required={true}
              onChange={handleChange}
              value={state.username}
              autoComplete="username"
            />
          </div>
          <PasswordInput
            label={props.passwordLabel ?? "Heslo"}
            name="password"
            required={true}
            onChange={handleChange}
            value={state.password}
            isInvalid={state.password === ""}
          />
          {!props.rememberDisabled && (
            <Checkbox
              name="remember"
              onChange={handleChange}
              value={state.remember}
              label={"Zapamätaj si ma"}
            />
          )}
          <Button
            primary={true}
            disabled={props.isLoading}
            label="Prihlásiť"
            type="submit"
          />
          {props.showForgotPassword && (
            <Button
              disabled={props.isLoading || state.username === null || state.username === ""}
              label="Zabudnuté heslo"
              onClick={handleCreateCode}
            />
          )}
          {children}
        </form>
      )
    }
  }
  const { loginFooter } = props;

  return (
    <>
      {props.backgroundUrl && (<div className="login-background"><img src={props.backgroundUrl} alt="" /></div>)}
      <div className="login-container">
        <div className="login-card">
          <div className="login-content">
            <div className="login-graphics">
              <div className="login-version-hidden">Verzia {getClientVersion()}</div>
              <div><img src={props.logoUrl} alt="" /></div>
              <div className="login-title">{props.appTitle}</div>
              <div className="">{props.appFullName}</div>
              <div className="login-version">Verzia {getClientVersion()}</div>
            </div>
            {renderLogin()}
          </div>
          {loginFooter && <div className="login-footer">
            {loginFooter}
          </div>
          }
        </div>
        <LoadingPanel isLoading={props.isLoading || state.isLoading} />
        <ConfirmDialog
          isOpen={state.isOpenAlert}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          text={"Na Váš Email Vám bol odoslaný kód na jednorazové prihlásenie."}
        />
      </div>
    </>
  );
}

Login.propTypes = {
  user: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  logoUrl: PropTypes.string,
  backgroundUrl: PropTypes.string,
  appTitle: PropTypes.string,
  appFullName: PropTypes.string,
  rememberDisabled: PropTypes.bool,
  signIn: PropTypes.func.isRequired,
  verifyGUID: PropTypes.func,
  oAuthLogin: PropTypes.func,
  userNameLabel: PropTypes.string,
  passwordLabel: PropTypes.string,
  afterLoginlocation: PropTypes.object,
  showForgotPassword: PropTypes.bool,
  loginFooter: PropTypes.element,
  showMsg: PropTypes.func.isRequired
};

export default withRouter(withStorage(withRequests(Login)));